import * as httpx from "httpx"
import * as uuid from "uuid"
import { kernels } from "brandguard/api"
import { CancellablePromise } from "real-cancellable-promise"
import {
	DocumentKernelSettingsCreateRequest,
	DocumentKernelSettingsCreateResponse,
	DocumentKernelSettingsResponse,
	DocumentKernelSearchRequest,
	DocumentKernelSearchResponse,
} from "brandguard/brandguard.document_pb"

import { DocumentKernelSetting, KernelMetadata } from "brandguard/brandguard.kernel_pb"

export namespace config {
	export function zero(d: Partial<DocumentKernelSetting> = {}): DocumentKernelSetting {
		return {
			id: "",
			threshold: 100,
			doc_brand_voice: {
				enabled: false,
			},
			doc_nudity: {
				enabled: true,
			},
			doc_violence: {
				enabled: false,
			},
			doc_racism: {
				enabled: false,
			},
			doc_grammar: {
				enabled: false,
			},
			doc_lookandfeel: {
				enabled: false,
				rules: [],
			},
			doc_logo_detection: {
				enabled: false,
				logos: [],
			},
			doc_font_detection: {
				enabled: false,
				fonts: [],
			},
			doc_background_colors: {
				enabled: false,
				colors: [],
			},
			doc_rules: {
				enabled: false,
				rules: [],
			},
			brand_consistency: {
				enabled: true,
			},
			product_detection: {
				enabled: false,
			},
			...d,
		}
	}

	export function latest(
		bid: string,
		id: string,
		...options: httpx.option[]
	): CancellablePromise<DocumentKernelSettingsResponse> {
		return httpx.get(
			`${httpx.urlstorage.host()}/brands/${bid}/bg/document/kernel/${id}/settings/${uuid.NIL}`,
			{},
			...options,
		)
	}
}

export namespace searches {
	export function request(d: Partial<DocumentKernelSearchRequest> = {}): DocumentKernelSearchRequest {
		return {
			offset: 0n,
			brand_id: "",
			limit: 100n,
			...d,
		}
	}

	export function response(d: Partial<DocumentKernelSearchResponse> = {}): DocumentKernelSearchResponse {
		return {
			next: request(),
			items: [],
			...d,
		}
	}
}

export function search(
	bid: string,
	req: DocumentKernelSearchRequest,
	...options: httpx.option[]
): CancellablePromise<DocumentKernelSearchResponse> {
	return httpx
		.get<DocumentKernelSearchResponse>(`${httpx.urlstorage.host()}/brands/${bid}/bg/document/kernel/`, req, ...options)
		.then((r) => ({
			...r,
			items: (r.items || []).map(kernels.metadata.zero),
		}))
}

export function create_version(
	bid: string,
	id: string,
	req: DocumentKernelSettingsCreateRequest,
	...options: httpx.option[]
): CancellablePromise<DocumentKernelSettingsCreateResponse> {
	return httpx.post(`${httpx.urlstorage.host()}/brands/${bid}/bg/document/kernel/${id}/settings`, req, ...options)
}

export function rename(
	bid: string,
	id: string,
	version: string,
	name: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata> {
	return httpx.patch(
		`${httpx.urlstorage.host()}/brands/${bid}/bg/document/kernel/${id}/name/${version}`,
		{ name },
		...options,
	)
}

export function reactivate(
	bid: string,
	id: string,
	version: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata> {
	return httpx.post(
		`${httpx.urlstorage.host()}/brands/${bid}/bg/document/kernel/${id}/reactivate/${version}`,
		{},
		...options,
	)
}

export function getactive(
	bid: string,
	id: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata | null> {
	return httpx
		.get<{ kernel?: KernelMetadata }>(
			`${httpx.urlstorage.host()}/brands/${bid}/bg/document/kernel/${id}/${uuid.NIL}`,
			{},
			...options,
		)
		.then((r) => r?.kernel || null)
		.catch(() => Promise.resolve(null))
}

export type { KernelMetadata }
